import React, { useEffect, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { loadConfig } from "../helpers/config";
import LoadingSpinner from "../components/LoadingSpinner";

function AddCheck() {
  let { toolId } = useParams();
  let navigate = useNavigate();
  const location = useLocation();
  const todayStr = new Date().toISOString().slice(0, 10);
  const initialValues = {
    checkdate: todayStr,
    checkresult: "",
    notes: ""
  };
  const [storagePosition, setStoragePosition] = useState("");
  // storageOptions must be initialized after ToolObject is defined
  // Remove storageOptions from here; will initialize inside render

  const [ToolObject, setToolObject] = useState({});
  const [apiUrl, setApiUrl] = useState("");
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [editingCheck, setEditingCheck] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);

  useEffect(() => {
    async function fetchConfigAndTool() {
      const config = await loadConfig();
      setApiUrl(config.APIURL);
      setLoadingConfig(false);
      const response = await axios.get(`${config.APIURL}/tool/${toolId}`, {
        headers: { accessToken: localStorage.getItem("accessToken") }
      });
      setToolObject(response.data);
      setStoragePosition(response.data.storagePosition || "");
      // Fetch categories for the toolsgroup
      if (response.data.ToolsGroup && response.data.ToolsGroup.id) {
        try {
          const catRes = await axios.get(`${config.APIURL}/categories/toolsgroup/${response.data.ToolsGroup.id}`, {
            headers: { accessToken: localStorage.getItem("accessToken") }
          });
          setCategories(catRes.data);
          // If categoryId is passed via location.state, use it; otherwise default to first
          if (catRes.data.length > 0) {
            if (location.state && location.state.categoryId) {
              setSelectedCategoryId(location.state.categoryId);
            } else {
              setSelectedCategoryId(catRes.data[0].id);
            }
          }
        } catch {
          setCategories([]);
        }
      }
      // If checkId is present, load the unfinished check
      if (location.state && location.state.checkId) {
        const checkRes = await axios.get(`${config.APIURL}/checks/${location.state.checkId}`, {
          headers: { accessToken: localStorage.getItem("accessToken") }
        });
        setEditingCheck(checkRes.data);
      }
    }
    fetchConfigAndTool();
  }, [toolId, location.state]);

  const validationSchema = Yup.object().shape({
    checkdate: Yup.string().required("Bitte Datum wählen!").matches(/^\d{4}-\d{2}-\d{2}$/, "Ungültiges Datum"),
    checkresult: Yup.string().required("Bitte gültiges Prüfergebnis auswählen!"),
    notes: Yup.string()
  });

  // Prepare dynamic fields for custom checks from selected category
  let customChecks = [];
  if (categories.length > 0 && selectedCategoryId) {
    const selectedCat = categories.find(cat => cat.id === selectedCategoryId);
    if (selectedCat && Array.isArray(selectedCat.customChecks)) {
      customChecks = selectedCat.customChecks;
    }
  }
  // Build initial values for Formik
  // Use short keys for Formik values
  const dynamicInitials = customChecks.reduce((acc, field, idx) => {
    const key = `customcheck-${idx}`;
    if (field.type === "checkbox") {
      acc[key] = false;
    } else {
      acc[key] = "";
    }
    return acc;
  }, {});
  let allInitialValues = { ...initialValues, ...dynamicInitials };
  // If editing, use check data
  if (editingCheck) {
    // Parse customChecks
    let parsedCustom = editingCheck.customChecks;
    if (typeof parsedCustom === "string") {
      try {
        parsedCustom = JSON.parse(parsedCustom);
        if (typeof parsedCustom === "string") {
          parsedCustom = JSON.parse(parsedCustom);
        }
      } catch {
        parsedCustom = {};
      }
    }
    // Ensure all fields are defined
    let safeCustom = {};
    customChecks.forEach((field, idx) => {
      let val = parsedCustom[field.name];
      const key = `customcheck-${idx}`;
      if (field.type === "checkbox") {
        if (typeof val === "boolean") {
          safeCustom[key] = val;
        } else if (typeof val === "string") {
          safeCustom[key] = val === "Ja" ? true : (val === "Nein" ? false : false);
        } else {
          safeCustom[key] = false;
        }
      } else if (field.type === "number") {
        if (typeof val === "number") {
          safeCustom[key] = val.toString();
        } else if (typeof val === "string") {
          safeCustom[key] = val;
        } else {
          safeCustom[key] = "";
        }
      } else {
        safeCustom[key] = typeof val === "string" ? val : "";
      }
    });
    allInitialValues = {
      ...allInitialValues,
      ...safeCustom,
      checkdate: editingCheck.checkdate ? editingCheck.checkdate.slice(0, 10) : todayStr,
      checkresult: typeof editingCheck.checkresult === "string" ? editingCheck.checkresult : "",
      notes: typeof editingCheck.notes === "string" ? editingCheck.notes : ""
    };
  }

  // Final pass: ensure all fields are defined
  Object.keys(allInitialValues).forEach(key => {
    if (typeof allInitialValues[key] === "undefined" || allInitialValues[key] === null) {
      // If checkbox, set to false; else, set to ""
      if (key.startsWith("customcheck-")) {
        allInitialValues[key] = "";
      }
    }
  });

  // Build validation schema for dynamic fields
  const dynamicValidation = customChecks.reduce((acc, field, idx) => {
    const key = `customcheck-${idx}`;
    if (field.type === "checkbox") {
      if (field.required) {
        acc[key] = Yup.boolean().oneOf([true], "Pflichtfeld: Bitte Haken setzen");
      } else {
        acc[key] = Yup.boolean();
      }
    } else if (field.required) {
      acc[key] = Yup.string().required("Pflichtfeld");
    } else {
      acc[key] = Yup.string();
    }
    return acc;
  }, {});
  const allValidationSchema = Yup.object().shape({
    ...validationSchema.fields,
    ...dynamicValidation
  });

  // Save as draft or finish
  const [saveError, setSaveError] = useState("");
  const handleSave = async (values, { setSubmitting }) => {
    setSaveError("");
    try {
      await saveCheck(values, false);
      setSubmitting(false);
      navigate(`/Tool/${ToolObject.id}`);
    } catch (err) {
      setSubmitting(false);
      let serverMsg = "";
      if (err.response && err.response.data) {
        serverMsg = err.response.data.message || err.response.data.error || "";
      }
      if (err.response && err.response.status === 400) {
        setSaveError("Fehler: Die Eingaben sind ungültig oder unvollständig." + (serverMsg ? `\n${serverMsg}` : ""));
      } else if (serverMsg) {
        setSaveError(serverMsg);
      } else {
        setSaveError("Speichern fehlgeschlagen. Bitte erneut versuchen.");
      }
    }
  };

  const handleFinish = async (values, { setSubmitting }) => {
    setSaveError("");
    try {
      const checkId = await saveCheck(values, true);
      setSubmitting(false);
      navigate(`/Check/${checkId}`);
    } catch (err) {
      setSubmitting(false);
      let serverMsg = "";
      if (err.response && err.response.data) {
        serverMsg = err.response.data.message || err.response.data.error || "";
      }
      if (err.response && err.response.status === 400) {
        setSaveError("Fehler: Die Eingaben sind ungültig oder unvollständig." + (serverMsg ? `\n${serverMsg}` : ""));
      } else if (serverMsg) {
        setSaveError(serverMsg);
      } else {
        setSaveError("Speichern fehlgeschlagen. Bitte erneut versuchen.");
      }
    }
  };

  const saveCheck = async (data, finished) => {
    // checkdate is already set by the form
    data["ToolId"] = ToolObject.id;
    // Flatten custom check values directly into customChecks object
    const customCheckValues = {};
    customChecks.forEach((field, idx) => {
      let value = data[`customcheck-${idx}`];
      if (field.type === "checkbox") {
        value = value === true ? "Ja" : "Nein";
      } else if (field.type === "number") {
        value = value === "" ? "" : String(value);
      }
      customCheckValues[field.name] = value;
      // Remove temporary Formik field from data
      delete data[`customcheck-${idx}`];
    });
    // customChecks should be a flat key-value object
    data["customChecks"] = customCheckValues;
    // Set inspector from login username
    data["inspector"] = localStorage.getItem("username");
    data["finished"] = finished;
    // Save selected categoryId with the check
    data["categoryId"] = selectedCategoryId;
    if (editingCheck && editingCheck.id) {
      // Use PUT to update unfinished check
      const response = await axios.put(`${apiUrl}/checks/${editingCheck.id}`, data, {
        headers: { accessToken: localStorage.getItem("accessToken") }
      });
      return response.data.id || editingCheck.id;
    } else {
      // Use POST to create new check
      const response = await axios.post(`${apiUrl}/checks`, data, {
        headers: { accessToken: localStorage.getItem("accessToken") }
      });
      return response.data.id;
    }
  };

  if (toolId < 1) {
    throw new Error("Missing toolId");
  }

  let toolname = "";
  let serialnumber = "";
  if ("ToolsGroup" in ToolObject && "groupname" in ToolObject.ToolsGroup) {
    toolname = ToolObject.ToolsGroup.groupname;
    serialnumber = ToolObject.serialnumber;
  }

  if (loadingConfig) {
    return <LoadingSpinner text="Lade Konfiguration..." />;
  }

  // Prevent rendering until ToolObject and categories are loaded
  if (!ToolObject || !ToolObject.ToolsGroup || loadingConfig || categories.length === 0) {
    return <LoadingSpinner text="Lade Gerätedaten..." />;
  }

  // Prevent checks on discarded tools
  if (ToolObject.isDiscarded === true) {
    return (
      <div className="container">
        <div className="alert alert-warning">
          <h4>Prüfung nicht möglich</h4>
          <p>Dieses Werkzeug wurde ausgesondert und kann nicht mehr geprüft werden.</p>
          <button className="btn btn-primary" onClick={() => navigate(`/tool/${toolId}`)}>
            Zurück zur Werkzeugansicht
          </button>
        </div>
      </div>
    );
  }

  let storageOptions = [];
  if (ToolObject.ToolsGroup && ToolObject.ToolsGroup.storagePositions) {
    if (Array.isArray(ToolObject.ToolsGroup.storagePositions)) {
      storageOptions = ToolObject.ToolsGroup.storagePositions;
    } else if (typeof ToolObject.ToolsGroup.storagePositions === "string") {
      try {
        storageOptions = JSON.parse(ToolObject.ToolsGroup.storagePositions);
      } catch {
        storageOptions = [];
      }
    }
  }

  return (
    <div className="container">
      <div className="card">
        <div className="card-header">
          Prüfung von {toolname} {serialnumber}
        </div>
        <div className="card-body">
          {/* Category selection if multiple exist */}
          {categories.length > 1 && (
            <div className="form-group">
              <label htmlFor="categorySelect">Prüfkategorie wählen:</label>
              <select
                id="categorySelect"
                className="form-control"
                value={selectedCategoryId || ""}
                onChange={e => setSelectedCategoryId(Number(e.target.value))}
              >
                {[...categories].sort((a, b) => a.name.localeCompare(b.name)).map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
          )}
          <Formik
            initialValues={allInitialValues}
            validationSchema={allValidationSchema}
            onSubmit={handleFinish}
            enableReinitialize={true}
          >
            {({ isSubmitting, values, setSubmitting }) => (
              <Form>
                {saveError && (
                  <div style={{
                    background: '#dc3545',
                    color: 'white',
                    padding: '10px',
                    borderRadius: '4px',
                    border: '1px solid #b71c1c',
                    marginBottom: '10px',
                    fontWeight: 'bold',
                    whiteSpace: 'pre-line'
                  }} role="alert">
                    {saveError}
                  </div>
                )}
                <div className="form-group">
                  <label htmlFor="checkdate">Prüfdatum:</label>
                  <Field
                    id="checkdate"
                    name="checkdate"
                    type="date"
                    className="form-control"
                  />
                  <ErrorMessage name="checkdate" component="span" className="error-message" />
                </div>
                <div className="form-group">
                  <label htmlFor="storagePosition">Lagerort:</label>
                  <select
                    id="storagePosition"
                    name="storagePosition"
                    className="form-control"
                    value={storagePosition}
                    onChange={e => setStoragePosition(e.target.value)}
                    onBlur={async (e) => {
                      if (storagePosition !== ToolObject.storagePosition) {
                        try {
                          await axios.put(`${apiUrl}/tool/${ToolObject.id}`, { storagePosition }, {
                            headers: { accessToken: localStorage.getItem("accessToken") }
                          });
                          setToolObject({ ...ToolObject, storagePosition });
                        } catch (err) {
                          // Optionally handle error
                        }
                      }
                    }}
                  >
                    <option value="">Bitte wählen</option>
                    {[...storageOptions].sort((a, b) => a.localeCompare(b)).map((opt, idx) => (
                      <option key={idx} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
                {/* Render dynamic custom check fields */}
                {customChecks.map((field, idx) => (
                  <div className="form-group" key={field.name}>
                    {/* Use a short unique id for input, allow long label text */}
                    <label htmlFor={`customcheck-${idx}`}>{field.description || field.name}:</label>
                    {field.type === "checkbox" ? (
                      <Field name={`customcheck-${idx}`}>
                        {({ field: f, form }) => (
                          <input
                            id={`customcheck-${idx}`}
                            type="checkbox"
                            checked={typeof form.values[`customcheck-${idx}`] === "boolean" ? form.values[`customcheck-${idx}`] : false}
                            onChange={e => {
                              form.setFieldValue(`customcheck-${idx}`, e.target.checked);
                            }}
                            className="form-control"
                          />
                        )}
                      </Field>
                    ) : field.type === "dropdown" ? (
                      <Field name={`customcheck-${idx}`}>
                        {({ field: f, form }) => (
                          <select
                            id={`customcheck-${idx}`}
                            className="form-control"
                            value={form.values[`customcheck-${idx}`] ?? ""}
                            onChange={f.onChange}
                            onBlur={f.onBlur}
                          >
                            <option value="">Bitte wählen</option>
                            {[...(field.options || [])].sort((a, b) => a.localeCompare(b)).map((opt, i) => (
                              <option key={i} value={opt}>{opt}</option>
                            ))}
                          </select>
                        )}
                      </Field>
                    ) : field.type === "number" ? (
                      <Field
                        id={`customcheck-${idx}`}
                        name={`customcheck-${idx}`}
                        type="number"
                        className="form-control"
                        min={field.min !== undefined ? field.min : undefined}
                        max={field.max !== undefined ? field.max : undefined}
                        step={field.step !== undefined ? field.step : "any"}
                      />
                    ) : (
                      <Field name={`customcheck-${idx}`}>
                        {({ field: f, form }) => (
                          <input
                            id={`customcheck-${idx}`}
                            type={field.type || "text"}
                            className="form-control"
                            value={form.values[`customcheck-${idx}`] || ""}
                            onChange={f.onChange}
                            onBlur={f.onBlur}
                          />
                        )}
                      </Field>
                    )}
                    <ErrorMessage name={`customcheck-${idx}`} component="span" className="error-message" />
                  </div>
                ))}
                <div className="form-group">
                  <label htmlFor="checkresult">Prüfergebnis:</label>
                  <Field
                    id="checkresult"
                    name="checkresult"
                    as="select"
                    className="form-control"
                  >
                    <option value=""></option>
                    <option value="OK">OK</option>
                    <option value="FAILED">Durchgefallen</option>
                  </Field>
                  <ErrorMessage name="checkresult" component="span" className="error-message" />
                </div>

                <div className="form-group">
                  <label htmlFor="notes">Notizen:</label>
                  <Field
                    id="notes"
                    name="notes"
                    as="textarea"
                    className="form-control"
                  />
                  <ErrorMessage name="notes" component="span" className="error-message" />
                </div>

                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={isSubmitting}
                  onClick={() => handleSave(values, { setSubmitting })}
                  style={{ marginRight: "10px" }}
                >
                  Speichern
                </button>
                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                  Fertigstellen
                </button>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
}

export default AddCheck;
